import React from "react";

import { Link } from "@remix-run/react";
import { Shield, Heart, Swords, Users, HelpCircle, Dot } from "lucide-react";

import { Card, CardContent } from "~/components/ui/card";
import { A, H3, P } from "~/components/ui/typography";
import type { TPart } from "~/database/types";
import {
    isValidRelationshipType,
    getRelationshipLabel,
} from "~/features/parts/constants";

interface PartRelationship {
    relationship: {
        id: string;
        type: string;
        description: string;
        sourcePartInteractionId: string;
        targetPartInteractionId: string;
    };
    targetPart: Pick<TPart, "id" | "name"> | null;
    sourcePart: Pick<TPart, "id" | "name"> | null;
}

interface PartRelationshipsDisplayProps {
    partId: string;
    relationships: PartRelationship[];
}

// Map relationship types to icons and colors
const relationshipTypeConfig: Record<
    string,
    { icon: React.ElementType; color: string; bgColor: string }
> = {
    Protective: { icon: Shield, color: "text-blue-600", bgColor: "bg-blue-50" },
    Supportive: {
        icon: Heart,
        color: "text-green-600",
        bgColor: "bg-green-50",
    },
    Conflictual: { icon: Swords, color: "text-red-600", bgColor: "bg-red-50" },
    Collaborative: {
        icon: Users,
        color: "text-purple-600",
        bgColor: "bg-purple-50",
    },
    Other: { icon: HelpCircle, color: "text-gray-600", bgColor: "bg-gray-50" },
};

// AIDEV-NOTE: Component to display part relationships with inline links to other parts
export function PartRelationshipsDisplay({
    partId,
    relationships,
}: PartRelationshipsDisplayProps) {
    if (!relationships || relationships.length === 0) {
        return null;
    }

    // Filter to only show relationships where the current part is the source
    const sourceRelationships = relationships.filter(
        (rel) => rel.sourcePart?.id === partId,
    );

    if (sourceRelationships.length === 0) {
        return null;
    }

    const getTypeConfig = (type: string) =>
        relationshipTypeConfig[type] || relationshipTypeConfig["Other"];

    return (
        <div className="">
            {sourceRelationships.map((rel, index) => {
                const otherPart = rel.targetPart;
                const description = rel.relationship.description;
                const config = getTypeConfig(rel.relationship.type);

                if (!otherPart) {
                    return null;
                }

                // AIDEV-NOTE: Convert type to user-facing label if valid
                const displayType = isValidRelationshipType(
                    rel.relationship.type,
                )
                    ? getRelationshipLabel(rel.relationship.type)
                    : rel.relationship.type;

                return (
                    <P
                        key={`${rel.relationship.id}-${index}`}
                        className="group flex items-start gap-2"
                    >
                        <div className="flex-1">
                            <span className="">{displayType} </span>
                            <A href={`/parts/${otherPart.id}`} className="">
                                {otherPart.name}:
                            </A>
                            <span className=""> {description}</span>
                        </div>
                    </P>
                );
            })}
        </div>
    );
}
